import { useQueryClient } from "@tanstack/react-query";
import { generateUrlParamsFromObject } from "../methods/generateUrlParamsFromObject";
import { QueryNames } from "../_services/hooks/queryNames";
import { getAuthHeader } from "../_services/utils/getAuthHeader";

const useVahakHttpClient = () => {
    const queryClient = useQueryClient();

    const calculateAuthHeader = () => {
        const authHeaderObj = getAuthHeader();
        return authHeaderObj?.token.includes("undefined") ? {} : authHeaderObj;
    };

    function calculatePath<QueryParamObjType>(path: string, queryParamObj?: QueryParamObjType) {
        const _path = queryParamObj
            ? `${path}${path.includes("?") ? "" : "?"}${generateUrlParamsFromObject(queryParamObj)}`
            : path;
        return _path;
    }

    async function invokeHttpCall<ResponseDataType>(path: string, config: RequestInit) {
        const request = new Request(path, config);
        let responseJSON;
        try {
            const response = await fetch(request);
            responseJSON = await response.json();

            if (response.ok) {
                if (response.status === 208) {
                    throw responseJSON;
                }
                return Promise.resolve(responseJSON as ResponseDataType);
            } else {
                if (response.status === 401) queryClient.invalidateQueries(QueryNames.validateToken);
                // This will call the error block
                throw { statusCode: response.status, statusText: response.statusText, ...responseJSON };
            }
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async function GET<ResponseDataType, QueryParamObjType = unknown>(
        path: string,
        queryParamObj?: QueryParamObjType,
        customHeaders?: RequestInit["headers"]
    ): Promise<ResponseDataType> {
        const config = { headers: { ...customHeaders, ...calculateAuthHeader() } };
        const init = { method: "GET", ...config };
        const _path = calculatePath<QueryParamObjType>(path, queryParamObj);
        return invokeHttpCall<ResponseDataType>(_path, init);
    }

    async function POST<RequestBodyType, ResponseDataType, QueryParamObjType = unknown>(
        path: string,
        body: RequestBodyType | undefined,
        formData?: FormData | undefined,
        queryParamObj?: QueryParamObjType,
        customHeaders?: RequestInit["headers"]
    ): Promise<ResponseDataType> {
        const config = formData
            ? {
                  headers: { ...calculateAuthHeader() }
              }
            : {
                  headers: {
                      ...customHeaders,
                      ...calculateAuthHeader(),
                      Accept: "application/json",
                      "Content-Type": "application/json"
                  }
              };
        const init = { method: "POST", body: formData ? formData : JSON.stringify(body), ...config };
        const _path = calculatePath<QueryParamObjType>(path, queryParamObj);
        return invokeHttpCall<ResponseDataType>(_path, init);
    }

    async function PUT<RequestBodyType, ResponseDataType, QueryParamObjType = unknown>(
        path: string,
        body: RequestBodyType,
        queryParamObj?: QueryParamObjType,
        customHeaders?: RequestInit["headers"]
    ): Promise<ResponseDataType> {
        const config = {
            headers: {
                ...customHeaders,
                ...calculateAuthHeader(),
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        };
        const init = { method: "PUT", body: JSON.stringify(body), ...config };
        const _path = calculatePath<QueryParamObjType>(path, queryParamObj);
        return invokeHttpCall<ResponseDataType>(_path, init);
    }

    async function DELETE<ResponseDataType, QueryParamObjType = unknown>(
        path: string,
        queryParamObj?: QueryParamObjType,
        customHeaders?: RequestInit["headers"]
    ): Promise<ResponseDataType> {
        const config = { headers: { ...customHeaders, ...calculateAuthHeader() } };
        const init = { method: "DELETE", ...config };
        const _path = calculatePath<QueryParamObjType>(path, queryParamObj);
        return invokeHttpCall<ResponseDataType>(_path, init);
    }

    return {
        GET,
        PUT,
        POST,
        DELETE
    };
};

export default useVahakHttpClient;
